import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, BookOpen, Users, Globe, Award, FileText,
  TrendingUp, HeadphonesIcon, Sparkles, ArrowRight, CheckCircle,
  Plane, Rocket
} from 'lucide-react';

// Import ThemeContext
import { useTheme } from '../Home/ThemeContext';

export default function ServicesPage() {
  const navigate = useNavigate();

  const context = useTheme();
  const isDarkMode = context.isDarkMode || context.theme === 'dark';

  // ─── DATA ────────────────────────────────────────────────────────────────────

  const services = [
    {
      icon: GraduationCap,
      num: '01',
      title: 'University Search',
      description: 'AI-driven matching with 500+ top-tier global institutions.',
      features: ['Personalized Matching', 'Course Comparison', 'Admission Analysis', 'Virtual Tours'],
      accent: '#3B82F6',
      link: '/allcollege'
    },
    {
      icon: FileText,
      num: '02',
      title: 'Application Support',
      description: 'End-to-end guidance to craft the perfect application.',
      features: ['SOP/LOR Editing', 'Document Review', 'Real-time Tracking', 'Deadline Alerts'],
      accent: '#8B5CF6',
      link: null
    },
    {
      icon: Award,
      num: '03',
      title: 'Scholarship Desk',
      description: 'Unlock financial aid and merit-based scholarships globally.',
      features: ['Eligibility Check', 'Application Help', 'Financial Planning', 'Database Access'],
      accent: '#F59E0B',
      link: '/student/scholarships'
    },
    {
      icon: Plane,
      num: '04',
      title: 'Visa & Immigration',
      description: '99% success rate in visa processing and documentation.',
      features: ['Mock Interviews', 'Document Checklist', 'Travel Insurance', 'Pre-departure Brief'],
      accent: '#10B981',
      link: null
    },
    {
      icon: Users,
      num: '05',
      title: 'Career Counselling',
      description: 'Map your future with industry-expert counsellors via 1-on-1 sessions.',
      features: ['1-on-1 Sessions', 'Psychometric Tests', 'Career Roadmap', 'Profile Building'],
      accent: '#EC4899',
      link: '/services/counselling'
    },
    {
      icon: BookOpen,
      num: '06',
      title: 'Test Prep Coaching',
      description: 'Elite coaching for IELTS, TOEFL, GRE, GMAT and more.',
      features: ['Live Classes', 'Mock Tests', 'Study Material', 'Score Guarantee'],
      accent: '#EF4444',
      link: '/services/coaching'
    }
  ];

  const stats = [
    { icon: TrendingUp, value: '97%', label: 'Visa Success Rate' },
    { icon: Users, value: '10k+', label: 'Students Placed' },
    { icon: Globe, value: '50+', label: 'Countries Covered' },
    { icon: Award, value: '15+', label: 'Years Experience' }
  ];

  const counsellingDomains = [
    { emoji: '🧠', title: 'Mental Health', desc: 'Professional psychological support and well-being guidance.' },
    { emoji: '👶', title: 'Child Psychology', desc: 'Expert assessment and developmental counselling for children.' },
    { emoji: '💞', title: 'Relationship Guidance', desc: 'Navigating family, social, and interpersonal dynamics.' },
    { emoji: '🎯', title: 'Stream & Career Selection', desc: 'Data-backed guidance for choosing the right academic path.' },
    { emoji: '✈️', title: 'Study Abroad Planning', desc: 'End-to-end international education roadmap and planning.' },
    { emoji: '🏛️', title: 'College Counselling', desc: 'Personalized shortlisting and admission strategy.' },
  ];

  const coachingPrograms = [
    { tag: 'Engineering', label: 'IIT-JEE Main & Advanced', sub: 'Physics · Chemistry · Mathematics' },
    { tag: 'Medical', label: 'NEET Entrance', sub: 'Physics · Chemistry · Zoology · Botany' },
    { tag: 'Foundation', label: 'Foundation Courses', sub: 'Class 6–10 Early Development' },
    { tag: 'Defence', label: 'NDA Preparation', sub: 'Comprehensive Defence Entry Prep' },
    { tag: 'Government', label: 'Banking & SSC', sub: 'All Govt. Competitive Exams' },
    { tag: 'Board', label: 'CBSE · ISC · ICSE', sub: 'Class 6–12 All Subjects' },
  ];

  const trainingCourses = [
    'Professional & Technical Courses',
    'Language Training Programs',
    'Entrepreneurship Dev. (EDP)',
    'Industrial Training',
    'IT & Skill-Based Courses',
    'Hobby & Interest Learning',
    'Project Reports & Innovation',
    'AI & ML with IITs / IIMs',
  ];

  const partnerTypes = [
    { icon: '🏫', title: 'Schools & Colleges', desc: 'Government and private institutions seeking to expand outreach and integrate cutting-edge curriculum.' },
    { icon: '🧑‍🏫', title: 'Expert Counsellors', desc: 'Qualified counsellors and consultants delivering holistic guidance across mental health, career, and education.' },
    { icon: '🏢', title: 'CSR & Corporates', desc: 'MNCs and organizations mobilizing CSR funds for meaningful educational impact across communities.' },
    { icon: '🌐', title: 'Global Institutions', desc: 'International universities and institutes partnering for student placement, exchange, and collaboration.' },
  ];

  const steps = [
    { title: 'Consult', icon: HeadphonesIcon, desc: 'Free expert session to map your goals.' },
    { title: 'Profile', icon: FileText, desc: 'Build a compelling academic profile.' },
    { title: 'Shortlist', icon: BookOpen, desc: 'AI-matched universities and programs.' },
    { title: 'Apply', icon: Rocket, desc: 'Guided applications with document support.' },
    { title: 'Fly', icon: Plane, desc: 'Visa, travel prep, and campus landing.' },
  ];

  // ─── THEME TOKENS ─────────────────────────────────────────────────────────────
  const bg       = isDarkMode ? '#030712' : '#F8FAFF';
  const surface  = isDarkMode ? '#0F172A' : '#FFFFFF';
  const surface2 = isDarkMode ? '#1E293B' : '#EFF6FF';
  const border   = isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';
  const text1    = isDarkMode ? '#F1F5F9' : '#0F172A';
  const text2    = isDarkMode ? '#94A3B8' : '#64748B';
  const gold     = '#F59E0B';
  const blue     = '#3B82F6';

  return (
    <>
      {/* ── GLOBAL STYLES ─────────────────────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .vz-page { font-family: 'Plus Jakarta Sans', sans-serif; }
        .vz-display { font-family: 'Fraunces', serif; }

        @keyframes vz-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
        @keyframes vz-spin-slow { to{transform:rotate(360deg)} }
        @keyframes vz-pulse-ring {
          0%{transform:scale(1);opacity:0.6}
          100%{transform:scale(1.6);opacity:0}
        }
        @keyframes vz-fade-up {
          from{opacity:0;transform:translateY(32px)}
          to{opacity:1;transform:translateY(0)}
        }
        @keyframes vz-shimmer {
          0%{background-position:200% center}
          100%{background-position:-200% center}
        }
        @keyframes vz-border-glow {
          0%,100%{opacity:0.4} 50%{opacity:1}
        }

        .vz-fade-up { animation: vz-fade-up 0.7s ease both; }
        .vz-fade-up-d1 { animation-delay: 0.1s; }
        .vz-fade-up-d2 { animation-delay: 0.2s; }
        .vz-fade-up-d3 { animation-delay: 0.3s; }
        .vz-fade-up-d4 { animation-delay: 0.4s; }

        .vz-shimmer-text {
          background: linear-gradient(90deg, #F59E0B, #3B82F6, #8B5CF6, #F59E0B);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: vz-shimmer 4s linear infinite;
        }

        .vz-card-hover {
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.35s ease;
        }
        .vz-card-hover:hover { transform: translateY(-8px) scale(1.01); }

        .vz-stat-card:hover .vz-stat-icon { transform: scale(1.2) rotate(-6deg); }
        .vz-stat-icon { transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1); }

        .vz-step-dot::after {
          content: '';
          position: absolute; top: 50%; right: -50%;
          width: 100%; height: 2px;
          background: repeating-linear-gradient(90deg, #3B82F6 0 8px, transparent 8px 16px);
          transform: translateY(-50%);
        }
        .vz-step-dot:last-child::after { display: none; }

        .vz-tag {
          font-size: 0.65rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 3px 10px;
          border-radius: 999px;
        }

        .vz-diagonal-cut {
          clip-path: polygon(0 0, 100% 0, 100% 92%, 0 100%);
        }
        .vz-diagonal-cut-bottom {
          clip-path: polygon(0 8%, 100% 0, 100% 100%, 0 100%);
        }

        .vz-grid-bg {
          background-image:
            linear-gradient(rgba(59,130,246,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59,130,246,0.06) 1px, transparent 1px);
          background-size: 48px 48px;
        }

        .vz-noise {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
          opacity: 0.4;
        }

        .vz-glow-line {
          height: 2px;
          background: linear-gradient(90deg, transparent, #3B82F6, #8B5CF6, #F59E0B, transparent);
        }

        .vz-coaching-card:hover {
          border-color: #3B82F6 !important;
          background: rgba(59,130,246,0.08) !important;
        }
        .vz-coaching-card { transition: all 0.25s ease; }

        .vz-training-pill:hover {
          background: #3B82F6 !important;
          color: #fff !important;
          transform: scale(1.04);
        }
        .vz-training-pill { transition: all 0.2s ease; cursor: default; }

        .vz-partner-card:hover .vz-partner-icon {
          transform: scale(1.2) rotate(8deg);
        }
        .vz-partner-icon { transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1); }
      `}</style>

      <div className="vz-page" style={{ background: bg, color: text1, overflowX: 'hidden' }}>

        {/* ═══════════════════════════════════════════════════════
            SECTION 1 · HERO
        ════════════════════════════════════════════════════════ */}
        <section className="vz-diagonal-cut vz-grid-bg" style={{
          position: 'relative',
          paddingTop: '160px',
          paddingBottom: '120px',
          background: isDarkMode
            ? 'linear-gradient(135deg,#030712 0%,#0B1628 50%,#030712 100%)'
            : 'linear-gradient(135deg,#EFF6FF 0%,#F8FAFF 50%,#EEF2FF 100%)',
          overflow: 'hidden',
        }}>
          <div className="vz-noise" />

          {/* Orbs */}
          {isDarkMode && <>
            <div style={{ position:'absolute', top:'10%', left:'5%', width:480, height:480, borderRadius:'50%', background:'radial-gradient(circle,rgba(59,130,246,0.18) 0%,transparent 70%)', pointerEvents:'none' }} />
            <div style={{ position:'absolute', bottom:'5%', right:'8%', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle,rgba(245,158,11,0.14) 0%,transparent 70%)', pointerEvents:'none' }} />
          </>}

          {/* Floating badge */}
          <div className="vz-fade-up" style={{ display:'flex', justifyContent:'center', marginBottom:32 }}>
            <span style={{
              display:'inline-flex', alignItems:'center', gap:8,
              background: isDarkMode ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.12)',
              border: `1.5px solid ${gold}44`,
              color: gold, borderRadius:999, padding:'6px 20px',
              fontSize:13, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase',
            }}>
              <Sparkles style={{ width:14, height:14 }} />
              Powered by AESPL Eduzone · AI Education Ecosystem
            </span>
          </div>

          {/* Headline */}
          <div style={{ textAlign:'center', position:'relative', zIndex:2, padding:'0 16px' }}>
            <h1 className="vz-display vz-fade-up vz-fade-up-d1" style={{
              fontSize:'clamp(2.8rem, 7vw, 6rem)',
              fontWeight:900,
              lineHeight:1.06,
              marginBottom:24,
              color: text1,
            }}>
              Every Student.<br/>
              <span className="vz-shimmer-text">Every Dream.</span><br />
              Every Destination.
            </h1>

            <p className="vz-fade-up vz-fade-up-d2" style={{
              fontSize:'clamp(1rem,2vw,1.25rem)', color:text2,
              maxWidth:620, margin:'0 auto 40px', lineHeight:1.75,
            }}>
              Vidyazon is a comprehensive AI-powered education ecosystem — connecting students
              from Class 6 to PG with institutions, counsellors, and opportunities across India and the world.
            </p>

            <div className="vz-fade-up vz-fade-up-d3" style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:14 }}>
              <button
                onClick={() => navigate('/contact')}
                style={{
                  display:'inline-flex', alignItems:'center', gap:10,
                  padding:'14px 32px', borderRadius:999,
                  background:'linear-gradient(135deg,#3B82F6,#8B5CF6)',
                  color:'#fff', fontWeight:700, fontSize:15,
                  border:'none', cursor:'pointer',
                  boxShadow:'0 8px 32px rgba(59,130,246,0.35)',
                  transition:'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 16px 40px rgba(59,130,246,0.45)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 8px 32px rgba(59,130,246,0.35)'; }}
              >
                Get Expert Consultation <ArrowRight style={{ width:18, height:18 }} />
              </button>
              <button
                onClick={() => navigate('/login')}
                style={{
                  padding:'14px 32px', borderRadius:999,
                  background:'transparent',
                  color: text1, fontWeight:700, fontSize:15,
                  border: `2px solid ${border}`,
                  cursor:'pointer',
                  transition:'all 0.2s ease',
                  backdropFilter:'blur(8px)',
                  background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; }}
              >
                Student Login
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 2 · STATS STRIP
        ════════════════════════════════════════════════════════ */}
        <section style={{
          background: isDarkMode ? '#0A1628' : '#FFFFFF',
          borderTop:`1px solid ${border}`,
          borderBottom:`1px solid ${border}`,
          position:'relative', zIndex:10,
        }}>
          <div className="vz-glow-line" style={{ position:'absolute', top:0, left:0, right:0 }} />
          <div style={{ maxWidth:1100, margin:'0 auto', padding:'56px 24px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:32 }}>
              {stats.map((s, i) => (
                <div key={i} className="vz-stat-card" style={{ textAlign:'center' }}>
                  <div className="vz-stat-icon" style={{
                    width:52, height:52, borderRadius:16, margin:'0 auto 14px',
                    background: isDarkMode ? 'rgba(59,130,246,0.12)' : '#EFF6FF',
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <s.icon style={{ width:22, height:22, color:blue }} />
                  </div>
                  <div className="vz-display" style={{ fontSize:'2.6rem', fontWeight:900, color:text1, lineHeight:1 }}>
                    {s.value}
                  </div>
                  <div style={{ fontSize:13, color:text2, fontWeight:600, marginTop:6, letterSpacing:'0.04em' }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 3 · ABOUT VIDYAZON
        ════════════════════════════════════════════════════════ */}
        <section style={{ maxWidth:1100, margin:'0 auto', padding:'100px 24px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:64, alignItems:'center' }}>

            <div>
              <span style={{
                display:'inline-block', background:`${blue}18`, color:blue,
                fontSize:11, fontWeight:800, letterSpacing:'0.14em',
                textTransform:'uppercase', padding:'4px 14px', borderRadius:999, marginBottom:20,
              }}>What Is Vidyazon?</span>
              <h2 className="vz-display" style={{
                fontSize:'clamp(2rem,4vw,3rem)', fontWeight:900, lineHeight:1.12,
                marginBottom:24, color:text1,
              }}>
                A Holistic,<br />
                <em style={{ color:gold, fontStyle:'italic' }}>AI-Powered</em><br />
                Education Ecosystem
              </h2>
              <p style={{ fontSize:16, color:text2, lineHeight:1.85, marginBottom:20 }}>
                Vidyazon, a division of <strong style={{ color:text1 }}>Admitere Educational Services Private Limited</strong>, seamlessly
                connects educational institutions, students, parents, counsellors, and partners through a unified, technology-driven platform.
              </p>
              <p style={{ fontSize:16, color:text2, lineHeight:1.85 }}>
                Supporting students from <strong style={{ color:text1 }}>Class 6 to PG</strong> — both Indian and international — 
                Vidyazon delivers end-to-end admission support, expert counselling, academic coaching, 
                and professional training under one roof.
              </p>
            </div>

            {/* Feature Pillars */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              {[
                { color:'#3B82F6', label:'Admission Support', sub:'India & International' },
                { color:'#8B5CF6', label:'Expert Counselling', sub:'7+ Domains' },
                { color:'#F59E0B', label:'Exam Coaching', sub:'JEE · NEET · NDA · Boards' },
                { color:'#10B981', label:'Certifications', sub:'AI · ML · EDP · Language' },
              ].map((p, i) => (
                <div key={i} className="vz-card-hover" style={{
                  padding:24, borderRadius:20, border:`1.5px solid ${border}`,
                  background: surface,
                  boxShadow: isDarkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 4px 24px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background:p.color, marginBottom:14 }} />
                  <div style={{ fontWeight:800, fontSize:15, color:text1, marginBottom:4 }}>{p.label}</div>
                  <div style={{ fontSize:12, color:text2 }}>{p.sub}</div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 4 · CORE SERVICES GRID
        ════════════════════════════════════════════════════════ */}
        <section style={{
          background: isDarkMode
            ? 'linear-gradient(180deg,#0F172A 0%,#030712 100%)'
            : 'linear-gradient(180deg,#F0F7FF 0%,#F8FAFF 100%)',
          padding:'100px 24px',
          position:'relative', overflow:'hidden',
        }}>
          {isDarkMode && <div style={{
            position:'absolute', top:0, left:'50%', transform:'translateX(-50%)',
            width:900, height:500, borderRadius:'50%',
            background:'radial-gradient(circle,rgba(99,102,241,0.08) 0%,transparent 70%)',
            pointerEvents:'none',
          }} />}

          <div style={{ maxWidth:1200, margin:'0 auto' }}>
            <div style={{ textAlign:'center', marginBottom:72 }}>
              <span style={{
                display:'inline-block', background:`${gold}18`, color:gold,
                fontSize:11, fontWeight:800, letterSpacing:'0.14em',
                textTransform:'uppercase', padding:'4px 14px', borderRadius:999, marginBottom:16,
              }}>Our Services</span>
              <h2 className="vz-display" style={{ fontSize:'clamp(2rem,4vw,3.2rem)', fontWeight:900, color:text1, marginBottom:16 }}>
                Comprehensive Support,<br/>Every Step of the Way
              </h2>
              <p style={{ fontSize:16, color:text2, maxWidth:520, margin:'0 auto' }}>
                From first consultation to campus landing — Vidyazon covers the entire educational journey.
              </p>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))', gap:24 }}>
              {services.map((svc, i) => (
                <div key={i} className="vz-card-hover" style={{
                  background: surface,
                  border:`1.5px solid ${border}`,
                  borderRadius:24,
                  padding:36,
                  position:'relative',
                  overflow:'hidden',
                  boxShadow: isDarkMode ? '0 4px 32px rgba(0,0,0,0.4)' : '0 4px 32px rgba(0,0,0,0.06)',
                }}>
                  {/* Accent top bar */}
                  <div style={{
                    position:'absolute', top:0, left:0, right:0, height:3,
                    background:`linear-gradient(90deg,${svc.accent},${svc.accent}44)`,
                  }} />

                  {/* Number */}
                  <div className="vz-display" style={{
                    position:'absolute', top:20, right:24,
                    fontSize:'3.5rem', fontWeight:900, opacity:0.06, color:svc.accent,
                    lineHeight:1, userSelect:'none',
                  }}>{svc.num}</div>

                  {/* Icon */}
                  <div style={{
                    width:56, height:56, borderRadius:16, marginBottom:24,
                    background:`${svc.accent}18`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <svc.icon style={{ width:26, height:26, color:svc.accent }} />
                  </div>

                  <h3 style={{ fontSize:'1.25rem', fontWeight:800, color:text1, marginBottom:10 }}>
                    {svc.title}
                  </h3>
                  <p style={{ fontSize:14, color:text2, lineHeight:1.7, marginBottom:24 }}>
                    {svc.description}
                  </p>

                  <ul style={{ listStyle:'none', padding:0, margin:'0 0 28px', display:'flex', flexDirection:'column', gap:10 }}>
                    {svc.features.map((f, fi) => (
                      <li key={fi} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:text2 }}>
                        <CheckCircle style={{ width:16, height:16, color:svc.accent, flexShrink:0 }} />
                        <span style={{ fontWeight:600 }}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {svc.link && (
                    <button
                      onClick={() => navigate(svc.link)}
                      style={{
                        display:'inline-flex', alignItems:'center', gap:8,
                        padding:'10px 22px', borderRadius:999,
                        background:`${svc.accent}18`, color:svc.accent,
                        border:`1.5px solid ${svc.accent}44`,
                        fontWeight:700, fontSize:13, cursor:'pointer',
                        transition:'all 0.2s ease',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background=svc.accent; e.currentTarget.style.color='#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background=`${svc.accent}18`; e.currentTarget.style.color=svc.accent; }}
                    >
                      Explore <ArrowRight style={{ width:14, height:14 }} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 5 · EXPERT COUNSELLING
        ════════════════════════════════════════════════════════ */}
        <section style={{ padding:'100px 24px', background: bg, position:'relative' }}>
          <div style={{ maxWidth:1100, margin:'0 auto' }}>
            <div style={{ display:'grid', gridTemplateColumns:'380px 1fr', gap:80, alignItems:'start' }}>

              <div style={{ position:'sticky', top:120 }}>
                <span style={{
                  display:'inline-block', background:`#8B5CF618`, color:'#8B5CF6',
                  fontSize:11, fontWeight:800, letterSpacing:'0.14em',
                  textTransform:'uppercase', padding:'4px 14px', borderRadius:999, marginBottom:20,
                }}>Expert Counselling</span>
                <h2 className="vz-display" style={{
                  fontSize:'clamp(1.8rem,3.5vw,2.8rem)', fontWeight:900,
                  color:text1, lineHeight:1.15, marginBottom:20,
                }}>
                  Virtual Guidance<br />
                  <span style={{ color:'#8B5CF6' }}>From Qualified</span><br />
                  Professionals
                </h2>
                <p style={{ fontSize:15, color:text2, lineHeight:1.8, marginBottom:32 }}>
                  Access experienced, qualified counsellors across mental health, 
                  psychology, career planning, and international education — 
                  all from the comfort of your home.
                </p>
                <button
                  onClick={() => navigate('/services/counselling')}
                  style={{
                    display:'inline-flex', alignItems:'center', gap:10,
                    padding:'12px 28px', borderRadius:999,
                    background:'linear-gradient(135deg,#8B5CF6,#EC4899)',
                    color:'#fff', fontWeight:700, fontSize:14,
                    border:'none', cursor:'pointer',
                    boxShadow:'0 8px 24px rgba(139,92,246,0.35)',
                    transition:'transform 0.2s ease',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform='translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform='translateY(0)'}
                >
                  Book a Session <ArrowRight style={{ width:16, height:16 }} />
                </button>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
                {counsellingDomains.map((d, i) => (
                  <div key={i} className="vz-card-hover" style={{
                    padding:28, borderRadius:20,
                    background: surface,
                    border:`1.5px solid ${border}`,
                    boxShadow: isDarkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 4px 24px rgba(0,0,0,0.05)',
                  }}>
                    <div style={{ fontSize:32, marginBottom:16 }}>{d.emoji}</div>
                    <div style={{ fontWeight:800, fontSize:15, color:text1, marginBottom:8 }}>{d.title}</div>
                    <div style={{ fontSize:13, color:text2, lineHeight:1.65 }}>{d.desc}</div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 6 · ONLINE COACHING
        ════════════════════════════════════════════════════════ */}
        <section style={{
          padding:'100px 24px',
          background: isDarkMode
            ? 'linear-gradient(135deg,#0B1628 0%,#0F172A 100%)'
            : 'linear-gradient(135deg,#F0F7FF 0%,#F5F3FF 100%)',
          position:'relative', overflow:'hidden',
        }}>
          <div className="vz-noise" style={{ opacity: isDarkMode ? 0.4 : 0.2 }} />
          {isDarkMode && <>
            <div style={{ position:'absolute', top:'20%', right:'5%', width:350, height:350, borderRadius:'50%', background:'radial-gradient(circle,rgba(59,130,246,0.12) 0%,transparent 70%)', pointerEvents:'none' }} />
          </>}

          <div style={{ maxWidth:1100, margin:'0 auto', position:'relative', zIndex:2 }}>
            <div style={{ textAlign:'center', marginBottom:64 }}>
              <span style={{
                display:'inline-block', background:`${blue}18`, color:blue,
                fontSize:11, fontWeight:800, letterSpacing:'0.14em',
                textTransform:'uppercase', padding:'4px 14px', borderRadius:999, marginBottom:16,
              }}>Academic & Competitive</span>
              <h2 className="vz-display" style={{ fontSize:'clamp(2rem,4vw,3rem)', fontWeight:900, color:text1, marginBottom:14 }}>
                Online Coaching & Exam Preparation
              </h2>
              <p style={{ fontSize:15, color:text2, maxWidth:560, margin:'0 auto' }}>
                Structured, result-oriented programs for India's most competitive 
                entrance exams and board curricula — Class 6 to PG.
              </p>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:18 }}>
              {coachingPrograms.map((p, i) => (
                <div key={i} className="vz-coaching-card" style={{
                  padding:'24px 28px', borderRadius:18,
                  border:`1.5px solid ${border}`,
                  background: surface,
                  display:'flex', flexDirection:'column', gap:10,
                }}>
                  <span className="vz-tag" style={{
                    background:`${blue}18`, color:blue,
                    width:'fit-content',
                  }}>{p.tag}</span>
                  <div style={{ fontWeight:800, fontSize:17, color:text1 }}>{p.label}</div>
                  <div style={{ fontSize:13, color:text2 }}>{p.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 7 · TRAINING & CERTIFICATIONS
        ════════════════════════════════════════════════════════ */}
        <section style={{ padding:'100px 24px', background: bg }}>
          <div style={{ maxWidth:1100, margin:'0 auto' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:80, alignItems:'center' }}>

              <div>
                <span style={{
                  display:'inline-block', background:`${gold}18`, color:gold,
                  fontSize:11, fontWeight:800, letterSpacing:'0.14em',
                  textTransform:'uppercase', padding:'4px 14px', borderRadius:999, marginBottom:20,
                }}>Training & Certification</span>
                <h2 className="vz-display" style={{
                  fontSize:'clamp(2rem,4vw,2.8rem)', fontWeight:900,
                  color:text1, lineHeight:1.15, marginBottom:20,
                }}>
                  Future-Ready Skills<br />
                  <span style={{ color:gold }}>On Your Schedule</span>
                </h2>
                <p style={{ fontSize:15, color:text2, lineHeight:1.85, marginBottom:16 }}>
                  Flexible, hourly-format courses across professional, technical, and creative domains. 
                  Programs are designed for accessibility and real-world applicability.
                </p>
                <p style={{ fontSize:15, color:text2, lineHeight:1.85 }}>
                  Vidyazon also offers <strong style={{ color:text1 }}>AI & ML programs in collaboration with IITs, IIMs, 
                  and leading institutions</strong> in India and abroad — keeping learners ahead of the curve.
                </p>
              </div>

              {/* Pills */}
              <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
                {trainingCourses.map((t, i) => (
                  <span key={i} className="vz-training-pill" style={{
                    padding:'10px 20px', borderRadius:999,
                    border:`1.5px solid ${border}`,
                    background: surface,
                    fontSize:13, fontWeight:700, color:text1,
                    boxShadow: isDarkMode ? '0 2px 12px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.05)',
                  }}>{t}</span>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 8 · HOW IT WORKS
        ════════════════════════════════════════════════════════ */}
        <section style={{
          padding:'100px 24px',
          background: isDarkMode
            ? 'linear-gradient(135deg,#0F172A 0%,#030712 100%)'
            : 'linear-gradient(135deg,#EFF6FF 0%,#F5F3FF 100%)',
          overflow:'hidden', position:'relative',
        }}>
          <div style={{ maxWidth:1100, margin:'0 auto' }}>
            <div style={{ textAlign:'center', marginBottom:72 }}>
              <span style={{
                display:'inline-block', background:`${blue}18`, color:blue,
                fontSize:11, fontWeight:800, letterSpacing:'0.14em',
                textTransform:'uppercase', padding:'4px 14px', borderRadius:999, marginBottom:16,
              }}>The Journey</span>
              <h2 className="vz-display" style={{ fontSize:'clamp(2rem,4vw,3rem)', fontWeight:900, color:text1, marginBottom:14 }}>
                Your Path to a Dream University<br />
                <span style={{ color:blue }}>in 5 Simple Steps</span>
              </h2>
            </div>

            <div style={{
              display:'grid', gridTemplateColumns:'repeat(5,1fr)',
              gap:24, position:'relative',
            }}>
              {/* connector line */}
              <div style={{
                position:'absolute', top:40, left:'10%', right:'10%',
                height:2, background: isDarkMode
                  ? 'repeating-linear-gradient(90deg,#3B82F6 0 10px,transparent 10px 20px)'
                  : 'repeating-linear-gradient(90deg,#93C5FD 0 10px,transparent 10px 20px)',
                zIndex:0,
              }} />

              {steps.map((step, i) => (
                <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', position:'relative', zIndex:1 }}>
                  <div style={{
                    width:80, height:80, borderRadius:'50%',
                    background: isDarkMode ? '#0F172A' : '#FFF',
                    border:`3px solid ${isDarkMode ? '#1E293B' : '#DBEAFE'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    marginBottom:24,
                    boxShadow: isDarkMode ? '0 0 0 6px #030712' : '0 0 0 6px #EFF6FF',
                    transition:'all 0.3s ease',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor=blue; e.currentTarget.style.transform='scale(1.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = isDarkMode ? '#1E293B' : '#DBEAFE'; e.currentTarget.style.transform='scale(1)'; }}
                  >
                    <step.icon style={{ width:28, height:28, color:blue }} />
                  </div>
                  <div style={{ fontSize:10, fontWeight:800, letterSpacing:'0.12em', color:blue, textTransform:'uppercase', marginBottom:6 }}>
                    Step {String(i+1).padStart(2,'0')}
                  </div>
                  <div style={{ fontWeight:800, fontSize:15, color:text1, marginBottom:6 }}>{step.title}</div>
                  <div style={{ fontSize:12, color:text2, lineHeight:1.6 }}>{step.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 9 · PARTNERSHIP
        ════════════════════════════════════════════════════════ */}
        <section style={{ padding:'100px 24px', background: bg }}>
          <div style={{ maxWidth:1100, margin:'0 auto' }}>

            <div style={{ textAlign:'center', marginBottom:64 }}>
              <span style={{
                display:'inline-block', background:`#10B98118`, color:'#10B981',
                fontSize:11, fontWeight:800, letterSpacing:'0.14em',
                textTransform:'uppercase', padding:'4px 14px', borderRadius:999, marginBottom:16,
              }}>Partner with Us</span>
              <h2 className="vz-display" style={{ fontSize:'clamp(2rem,4vw,3rem)', fontWeight:900, color:text1, marginBottom:14 }}>
                Who Can Partner<br />
                <span style={{ color:'#10B981' }}>with Vidyazon?</span>
              </h2>
              <p style={{ fontSize:15, color:text2, maxWidth:560, margin:'0 auto' }}>
                We collaborate with institutions, professionals, and organizations committed 
                to expanding access, improving outcomes, and shaping the future of education.
              </p>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:24 }}>
              {partnerTypes.map((p, i) => (
                <div key={i} className="vz-card-hover" style={{
                  padding:36, borderRadius:24,
                  background: surface,
                  border:`1.5px solid ${border}`,
                  textAlign:'center',
                  boxShadow: isDarkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 4px 24px rgba(0,0,0,0.05)',
                }}>
                  <div className="vz-partner-icon" style={{ fontSize:40, marginBottom:20 }}>{p.icon}</div>
                  <h3 style={{ fontWeight:800, fontSize:17, color:text1, marginBottom:10 }}>{p.title}</h3>
                  <p style={{ fontSize:13, color:text2, lineHeight:1.7 }}>{p.desc}</p>
                </div>
              ))}
            </div>

            {/* CSR note */}
            <div style={{
              marginTop:40, padding:28, borderRadius:20,
              background: isDarkMode ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.05)',
              border:`1.5px dashed #10B98144`,
              display:'flex', alignItems:'center', gap:20,
            }}>
              <div style={{ fontSize:32 }}>🤝</div>
              <div>
                <div style={{ fontWeight:800, fontSize:15, color:text1, marginBottom:4 }}>
                  CSR & Institutional Collaboration
                </div>
                <div style={{ fontSize:13, color:text2, lineHeight:1.7 }}>
                  Organizations and MNCs can partner with Vidyazon to effectively utilize CSR funds and 
                  create meaningful educational impact across government and private schools, 
                  and technical institutions nationwide.
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 10 · COMMITMENT STRIP
        ════════════════════════════════════════════════════════ */}
        <section style={{
          padding:'64px 24px',
          background: isDarkMode ? '#0A1628' : '#F0F7FF',
          borderTop:`1px solid ${border}`,
          borderBottom:`1px solid ${border}`,
        }}>
          <div style={{ maxWidth:900, margin:'0 auto', textAlign:'center' }}>
            <div className="vz-display" style={{
              fontSize:'clamp(1.2rem,3vw,1.8rem)', fontWeight:700,
              color:text2, lineHeight:1.7,
              fontStyle:'italic',
            }}>
              "Vidyazon is committed to building a{' '}
              <span style={{ color:blue, fontStyle:'normal', fontWeight:900 }}>future-ready, AI-powered education ecosystem</span>
              {' '}that empowers learners, supports institutions, and guides individuals 
              at every stage of their academic and professional journey."
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════
            SECTION 11 · FINAL CTA
        ════════════════════════════════════════════════════════ */}
        <section style={{
          position:'relative', overflow:'hidden',
          padding:'120px 24px',
          background:'linear-gradient(135deg,#1D4ED8 0%,#7C3AED 50%,#DB2777 100%)',
        }}>
          {/* Orbs */}
          <div style={{ position:'absolute', top:'-20%', right:'-10%', width:500, height:500, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }} />
          <div style={{ position:'absolute', bottom:'-20%', left:'-10%', width:400, height:400, borderRadius:'50%', background:'rgba(0,0,0,0.1)', pointerEvents:'none' }} />
          <div style={{ position:'absolute', inset:0, backgroundImage:'repeating-linear-gradient(90deg,rgba(255,255,255,0.03) 0 1px,transparent 1px 60px), repeating-linear-gradient(0deg,rgba(255,255,255,0.03) 0 1px,transparent 1px 60px)', pointerEvents:'none' }} />

          <div style={{ maxWidth:700, margin:'0 auto', textAlign:'center', position:'relative', zIndex:2 }}>
            <div className="vz-display" style={{
              fontSize:'clamp(2.4rem,6vw,4.5rem)',
              fontWeight:900, color:'#fff', lineHeight:1.08, marginBottom:24,
            }}>
              Ready to Take<br />Flight?
            </div>
            <p style={{ fontSize:18, color:'rgba(255,255,255,0.85)', marginBottom:48, lineHeight:1.75 }}>
              Join 10,000+ students who trust Vidyazon for their study abroad dreams 
              and academic success. Start your journey today — no commitment required.
            </p>
            <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:16 }}>
              <button
                onClick={() => navigate('/contact')}
                style={{
                  display:'inline-flex', alignItems:'center', gap:10,
                  padding:'16px 36px', borderRadius:999,
                  background:'#fff', color:'#1D4ED8',
                  fontWeight:800, fontSize:16,
                  border:'none', cursor:'pointer',
                  boxShadow:'0 8px 32px rgba(0,0,0,0.25)',
                  transition:'all 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px) scale(1.03)'; e.currentTarget.style.boxShadow='0 16px 48px rgba(0,0,0,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform='translateY(0) scale(1)'; e.currentTarget.style.boxShadow='0 8px 32px rgba(0,0,0,0.25)'; }}
              >
                <HeadphonesIcon style={{ width:20, height:20 }} />
                Speak to a Counsellor
              </button>
              <button
                onClick={() => navigate('/signup')}
                style={{
                  padding:'16px 36px', borderRadius:999,
                  background:'transparent',
                  color:'#fff', fontWeight:800, fontSize:16,
                  border:'2.5px solid rgba(255,255,255,0.5)',
                  cursor:'pointer',
                  transition:'all 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.12)'; e.currentTarget.style.transform='translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.transform='translateY(0)'; }}
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}